import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { parseJsonOrProperties } from '../../components/utils/parse-json-or-properties';
import {
	GetAIEvaluationsDocument,
	type GetAIEvaluationsQuery,
	GetAITracesDocument,
	type GetAITracesQuery,
	useGetAIEvaluationCategoriesQuery,
	useGetAITracesStatsQuery,
} from '../gql/_gen_/ai-tracer.gql';
import type { LLMEvaluation, LLMTrace } from '../types/ai-tracer-types';

/**
 * Frontend memory cache for transformed data
 * Prevents re-transforming the same data multiple times
 */
interface FrontendCacheEntry<T> {
	data: T;
	timestamp: number;
	targetRecords: number;
}

const FRONTEND_CACHE_TTL = 10 * 60 * 1000; // 10 minutes
const frontendTracesCache = new Map<string, FrontendCacheEntry<LLMTrace[]>>();
const frontendEvaluationsCache = new Map<
	string,
	FrontendCacheEntry<LLMEvaluation[]>
>();

function generateFrontendCacheKey(
	environmentFilter?: string,
	targetRecords?: number,
	dateFrom?: string,
	dateTo?: string,
): string {
	const dateKey =
		dateFrom || dateTo ? `_${dateFrom || 'noFrom'}_${dateTo || 'noTo'}` : '';
	return `${environmentFilter || 'default'}_${targetRecords || 1000}${dateKey}`;
}

function isFrontendCacheValid<T>(
	entry: FrontendCacheEntry<T>,
	targetRecords: number,
): boolean {
	return (
		Date.now() - entry.timestamp < FRONTEND_CACHE_TTL &&
		entry.targetRecords >= targetRecords
	);
}

/**
 * Cache invalidation utilities
 * Provides methods to clear caches when needed
 */
export function clearFrontendCache() {
	frontendTracesCache.clear();
	frontendEvaluationsCache.clear();
	console.log('Frontend cache cleared');
}

export function clearTracesCache() {
	frontendTracesCache.clear();
	console.log('Traces frontend cache cleared');
}

export function clearEvaluationsCache() {
	frontendEvaluationsCache.clear();
	console.log('Evaluations frontend cache cleared');
}

/**
 * Build hierarchical trace structure from flat list
 */
const buildTraceHierarchy = (traces: LLMTrace[]): LLMTrace[] => {
	type EnrichedTrace = LLMTrace & { _spanId: string; _parentSpanId?: string };

	const traceBySpanId = new Map<string, EnrichedTrace>();
	const rootTraces: LLMTrace[] = [];

	// First pass: create maps using both spanId (from metadata) and regular id
	for (const trace of traces) {
		const spanId = trace.metadata?.spanId || trace.id;
		const parentSpanId = trace.metadata?.parentSpanId || trace.parent_span_id;

		const enrichedTrace: EnrichedTrace = {
			...trace,
			children: [],
			// Store the actual spanId for hierarchy building
			_spanId: spanId,
			_parentSpanId: parentSpanId,
		};

		traceBySpanId.set(spanId, enrichedTrace);
	}

	// Second pass: build parent-child relationships using spanIds
	for (const trace of Array.from(traceBySpanId.values())) {
		if (trace._parentSpanId) {
			const parent = traceBySpanId.get(trace._parentSpanId);
			if (parent) {
				if (!parent.children) parent.children = [];
				// Remove temporary fields before adding as child
				const { _spanId, _parentSpanId, ...cleanTrace } = trace;
				parent.children.push(cleanTrace);
			} else {
				// Parent not found, treat as root trace
				const { _spanId, _parentSpanId, ...cleanTrace } = trace;
				rootTraces.push(cleanTrace);
			}
		} else {
			// No parent, treat as root trace
			const { _spanId, _parentSpanId, ...cleanTrace } = trace;
			rootTraces.push(cleanTrace);
		}
	}

	return rootTraces;
};

/**
 * Field transformation utilities for converting between GraphQL camelCase and frontend snake_case
 */
export const transformGQLTraceToFrontend = (
	gqlTrace: NonNullable<GetAITracesQuery['getAITraces']>[0],
): LLMTrace | null => {
	if (!gqlTrace?.id) return null;

	// Children will be populated by buildTraceHierarchy function

	return {
		id: gqlTrace.id,
		session_id: gqlTrace.sessionId || '',
		user_id: gqlTrace.userId || '',
		function_id: gqlTrace.functionId || '',
		request_id: gqlTrace.requestId || '',
		parent_span_id: gqlTrace.parentSpanId || undefined,
		model_name: gqlTrace.modelName || '',
		environment: gqlTrace.environment || '',
		system_prompt: gqlTrace.systemPrompt || '',
		input_messages: gqlTrace.inputMessages || '',
		output_content: gqlTrace.outputContent || '',
		input_tokens: gqlTrace.inputTokens || 0,
		output_tokens: gqlTrace.outputTokens || 0,
		total_tokens: gqlTrace.totalTokens || 0,
		total_cost: gqlTrace.totalCost || '0',
		latency_ms: gqlTrace.latencyMs || 0,
		status: gqlTrace.status || '',
		error_message: gqlTrace.errorMessage || undefined,
		metadata: gqlTrace.metadata
			? (() => {
					try {
						return parseJsonOrProperties(gqlTrace.metadata) || {};
					} catch (error) {
						console.warn('Error parsing trace metadata:', error);
						return {};
					}
				})()
			: {},
		created_at: gqlTrace.createdAt || new Date().toISOString(),
		children: undefined, // Will be set by buildTraceHierarchy
	};
};

export const transformGQLEvaluationToFrontend = (
	gqlEval: NonNullable<GetAIEvaluationsQuery['getAIEvaluations']>[0],
): LLMEvaluation | null => {
	if (!gqlEval?.id) return null;

	// Parse scores JSON to extract numeric values using optimized parser
	const parseScores = (scoresJson?: string | null) => {
		if (!scoresJson)
			return { overall: 0, accuracy: 0, relevance: 0, coherence: 0, safety: 0 };

		try {
			const parsed = parseJsonOrProperties(scoresJson);
			if (!parsed) {
				return {
					overall: 0,
					accuracy: 0,
					relevance: 0,
					coherence: 0,
					safety: 0,
				};
			}

			return {
				overall: parsed.overall || 0,
				accuracy: parsed.accuracy || 0,
				relevance: parsed.relevance || parsed.completeness || 0, // Map completeness to relevance if needed
				coherence: parsed.coherence || parsed.formatCompliance || 0, // Map formatCompliance to coherence if needed
				safety: parsed.safety || parsed.instructionFollowing || 0, // Map instructionFollowing to safety if needed
			};
		} catch (error) {
			console.warn('Error parsing scores:', error);
			return { overall: 0, accuracy: 0, relevance: 0, coherence: 0, safety: 0 };
		}
	};

	const scores = parseScores(gqlEval.scores);

	return {
		id: gqlEval.id,
		trace_id: gqlEval.traceId || '',
		category: gqlEval.category?.name || '',
		timestamp: gqlEval.createdAt || new Date().toISOString(),
		overall_score: scores.overall,
		categories: {
			accuracy: scores.accuracy,
			relevance: scores.relevance,
			coherence: scores.coherence,
			safety: scores.safety,
		},
		feedback: gqlEval.evaluationOutput || undefined,
		metadata: gqlEval.metadata
			? (() => {
					try {
						return parseJsonOrProperties(gqlEval.metadata) || undefined;
					} catch (error) {
						console.warn('Error parsing evaluation metadata:', error);
						return undefined;
					}
				})()
			: undefined,
		trace: gqlEval.trace
			? transformGQLTraceToFrontend(gqlEval.trace) || undefined
			: undefined,
	};
};

/**
 * Custom hook for AI Traces with concurrent chunked loading and date filtering
 * Loads multiple smaller chunks in parallel to avoid payload limits
 */
export const useAITraces = (
	_environmentFilter?: string,
	targetRecords = 1000,
	dateFrom?: string,
	dateTo?: string,
) => {
	const [allTraces, setAllTraces] = useState<LLMTrace[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [chunksLoaded, setChunksLoaded] = useState(0);
	const [totalChunks, setTotalChunks] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const client = useApolloClient();

	// Check frontend cache first
	const frontendCacheKey = generateFrontendCacheKey(
		_environmentFilter,
		targetRecords,
		dateFrom,
		dateTo,
	);
	const cachedData = frontendTracesCache.get(frontendCacheKey);

	const chunkSize = 100; // Smaller chunks for faster initial paint
	const numChunks = Math.ceil(targetRecords / chunkSize);

	// Create multiple queries for concurrent loading
	const queries = useMemo(() => {
		return Array.from({ length: numChunks }, (_, index) => ({
			limit: chunkSize,
			offset: index * chunkSize,
			dateFrom,
			dateTo,
		}));
	}, [numChunks, chunkSize, dateFrom, dateTo]);

	const loadAllTraces = useCallback(async () => {
		// Check frontend cache first
		if (cachedData && isFrontendCacheValid(cachedData, targetRecords)) {
			setAllTraces(cachedData.data);
			setChunksLoaded(numChunks);
			setTotalChunks(numChunks);
			return;
		}

		setIsLoading(true);
		setIsLoadingMore(false);
		setError(null);
		setAllTraces([]); // Clear existing data
		setChunksLoaded(0);
		setTotalChunks(numChunks);

		try {
			// Process chunks sequentially for progressive loading
			for (let i = 0; i < numChunks; i++) {
				const filter = queries[i];

				try {
					if (i === 0) {
						setIsLoading(true); // First chunk - show main loading
					} else {
						setIsLoading(false); // Subsequent chunks - show "loading more"
						setIsLoadingMore(true);
					}

					const { data } = await client.query({
						query: GetAITracesDocument,
						variables: { filter },
						errorPolicy: 'all',
						fetchPolicy: 'no-cache',
					});

					const chunkResults = data?.getAITraces || [];

					// Transform chunk data
					const transformedChunk = chunkResults
						.map(transformGQLTraceToFrontend)
						.filter(
							(trace: LLMTrace | null): trace is LLMTrace => trace !== null,
						);

					// Update state with new chunk (preserve existing hierarchy, integrate new traces)
					setAllTraces((prev) => {
						// If no previous data, just build hierarchy for new chunk
						if (prev.length === 0) {
							return buildTraceHierarchy(transformedChunk);
						}

						// Flatten previous hierarchical data to get all traces
						const flattenTraces = (traces: LLMTrace[]): LLMTrace[] => {
							const result: LLMTrace[] = [];
							for (const trace of traces) {
								// Add the parent trace (without children property for flattening)
								const { children, ...traceWithoutChildren } = trace;
								result.push(traceWithoutChildren);
								// Recursively add children
								if (children && children.length > 0) {
									result.push(...flattenTraces(children));
								}
							}
							return result;
						};

						const prevFlattened = flattenTraces(prev);
						const combined = [...prevFlattened, ...transformedChunk];
						return buildTraceHierarchy(combined);
					});

					setChunksLoaded(i + 1);

					// Small delay between chunks to show progressive loading
					if (i < numChunks - 1) {
						await new Promise((resolve) => setTimeout(resolve, 100));
					}
				} catch (chunkError) {
					console.error('Error loading trace chunk:', chunkError);
					// Continue with next chunk even if one fails
				}
			}

			// Store final result in cache
			setAllTraces((finalTraces) => {
				frontendTracesCache.set(frontendCacheKey, {
					data: finalTraces,
					timestamp: Date.now(),
					targetRecords,
				});
				return finalTraces;
			});
		} catch (err) {
			console.error('Error loading traces:', err);
			setError(err instanceof Error ? err.message : 'Unknown error');
		} finally {
			setIsLoading(false);
			setIsLoadingMore(false);
		}
	}, [
		queries,
		targetRecords,
		numChunks,
		chunkSize,
		client,
		cachedData,
		frontendCacheKey,
	]);

	// Load data on mount
	useEffect(() => {
		loadAllTraces();
	}, [loadAllTraces]);

	const refreshData = useCallback(() => {
		// Clear frontend cache on refresh
		frontendTracesCache.delete(frontendCacheKey);
		loadAllTraces();
	}, [loadAllTraces, frontendCacheKey]);

	return {
		traces: allTraces,
		isLoading,
		isLoadingMore,
		error,
		refreshData,
		chunksLoaded,
		totalChunks,
	};
};

/**
 * Custom hook for AI Evaluations with concurrent chunked loading and date filtering
 * Loads multiple smaller chunks in parallel to avoid payload limits
 */
export const useAIEvaluations = (
	_environmentFilter?: string,
	targetRecords = 1000,
	dateFrom?: string,
	dateTo?: string,
) => {
	const [allEvaluations, setAllEvaluations] = useState<LLMEvaluation[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [chunksLoaded, setChunksLoaded] = useState(0);
	const [totalChunks, setTotalChunks] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const client = useApolloClient();

	// Check frontend cache first
	const frontendCacheKey = generateFrontendCacheKey(
		_environmentFilter,
		targetRecords,
		dateFrom,
		dateTo,
	);
	const cachedData = frontendEvaluationsCache.get(frontendCacheKey);

	const chunkSize = 100; // Smaller chunks for faster initial paint
	const numChunks = Math.ceil(targetRecords / chunkSize);

	// Create multiple queries for concurrent loading
	const queries = useMemo(() => {
		return Array.from({ length: numChunks }, (_, index) => ({
			limit: chunkSize,
			offset: index * chunkSize,
			dateFrom,
			dateTo,
		}));
	}, [numChunks, chunkSize, dateFrom, dateTo]);

	const loadAllEvaluations = useCallback(async () => {
		// Check frontend cache first
		if (cachedData && isFrontendCacheValid(cachedData, targetRecords)) {
			setAllEvaluations(cachedData.data);
			setChunksLoaded(numChunks);
			setTotalChunks(numChunks);
			return;
		}

		setIsLoading(true);
		setIsLoadingMore(false);
		setError(null);
		setAllEvaluations([]); // Clear existing data
		setChunksLoaded(0);
		setTotalChunks(numChunks);

		try {
			// Process chunks sequentially for progressive loading
			for (let i = 0; i < numChunks; i++) {
				const filter = queries[i];

				try {
					if (i === 0) {
						setIsLoading(true); // First chunk - show main loading
					} else {
						setIsLoading(false); // Subsequent chunks - show "loading more"
						setIsLoadingMore(true);
					}

					const { data } = await client.query({
						query: GetAIEvaluationsDocument,
						variables: { filter },
						errorPolicy: 'all',
						fetchPolicy: 'no-cache',
					});

					const chunkResults = data?.getAIEvaluations || [];

					// Transform chunk data
					const transformedChunk = chunkResults
						.map(transformGQLEvaluationToFrontend)
						.filter(
							(evaluation: LLMEvaluation | null): evaluation is LLMEvaluation =>
								evaluation !== null,
						);

					// Update state with new chunk (append to existing, deduplicating by ID)
					setAllEvaluations((prev) => {
						const existingIds = new Set(prev.map((e: LLMEvaluation) => e.id));
						const newEvaluations = transformedChunk.filter(
							(e: LLMEvaluation) => !existingIds.has(e.id),
						);
						return [...prev, ...newEvaluations];
					});
					setChunksLoaded(i + 1);

					// Small delay between chunks to show progressive loading
					if (i < numChunks - 1) {
						await new Promise((resolve) => setTimeout(resolve, 100));
					}
				} catch (chunkError) {
					console.error('Error loading evaluation chunk:', chunkError);
					// Continue with next chunk even if one fails
				}
			}

			// Store final result in cache
			setAllEvaluations((finalEvaluations) => {
				frontendEvaluationsCache.set(frontendCacheKey, {
					data: finalEvaluations,
					timestamp: Date.now(),
					targetRecords,
				});
				return finalEvaluations;
			});
		} catch (err) {
			console.error('Error loading evaluations:', err);
			setError(err instanceof Error ? err.message : 'Unknown error');
		} finally {
			setIsLoading(false);
			setIsLoadingMore(false);
		}
	}, [
		queries,
		targetRecords,
		numChunks,
		chunkSize,
		client,
		cachedData,
		frontendCacheKey,
	]);

	// Load data on mount
	useEffect(() => {
		loadAllEvaluations();
	}, [loadAllEvaluations]);

	const refreshData = useCallback(() => {
		// Clear frontend cache on refresh
		frontendEvaluationsCache.delete(frontendCacheKey);
		loadAllEvaluations();
	}, [loadAllEvaluations, frontendCacheKey]);

	return {
		evaluations: allEvaluations,
		isLoading,
		isLoadingMore,
		error,
		refreshData,
		chunksLoaded,
		totalChunks,
	};
};

/**
 * Custom hook for AI Tracer Dashboard Statistics
 */
export const useAITracesStats = () => {
	const { data, loading, error, refetch } = useGetAITracesStatsQuery({
		errorPolicy: 'all',
		notifyOnNetworkStatusChange: true,
	});

	const stats = useMemo(() => {
		if (!data?.getAITracesStats) return null;

		return {
			totalTraces: data.getAITracesStats.totalTraces || 0,
			totalSessions: data.getAITracesStats.totalSessions || 0,
			totalCost: data.getAITracesStats.totalCost || '0.00',
			avgLatencyMs: data.getAITracesStats.avgLatencyMs || null,
			successRate: data.getAITracesStats.successRate || null,
			topModels: data.getAITracesStats.topModels || [],
		};
	}, [data?.getAITracesStats]); // Only re-run when stats data changes

	const refreshData = useCallback(() => {
		refetch();
	}, [refetch]);

	return {
		stats,
		isLoading: loading,
		error: error?.message || null,
		refreshData,
	};
};

/**
 * Custom hook for AI Evaluation Categories
 */
export const useAIEvaluationCategories = (activeOnly = true) => {
	const { data, loading, error, refetch } = useGetAIEvaluationCategoriesQuery({
		variables: { activeOnly },
		errorPolicy: 'all',
		notifyOnNetworkStatusChange: true,
	});

	const categories = useMemo(() => {
		if (!data?.getAIEvaluationCategories) return [];

		return data.getAIEvaluationCategories
			.filter((cat) => cat?.id && cat?.name)
			.map((cat) => ({
				id: cat!.id!,
				name: cat!.name!,
				description: cat!.description || null,
				scoringCriteria: cat!.scoringCriteria || null,
				isActive: cat!.isActive || false,
				createdAt: cat!.createdAt || new Date().toISOString(),
			}));
	}, [data?.getAIEvaluationCategories]); // Only re-run when categories data changes

	const refreshData = useCallback(() => {
		refetch();
	}, [refetch]);

	return {
		categories,
		isLoading: loading,
		error: error?.message || null,
		refreshData,
	};
};

/**
 * Comprehensive AI Tracer data hook that provides all data sources
 */
export const useAITracerDashboard = (environmentFilter = 'bestrev') => {
	const tracesHook = useAITraces(environmentFilter);
	const evaluationsHook = useAIEvaluations(environmentFilter);
	const statsHook = useAITracesStats();
	const categoriesHook = useAIEvaluationCategories();

	const refreshAll = useCallback(() => {
		tracesHook.refreshData();
		evaluationsHook.refreshData();
		statsHook.refreshData();
		categoriesHook.refreshData();
	}, [tracesHook, evaluationsHook, statsHook, categoriesHook]);

	const isLoading =
		tracesHook.isLoading ||
		evaluationsHook.isLoading ||
		statsHook.isLoading ||
		categoriesHook.isLoading;

	const isLoadingMore =
		tracesHook.isLoadingMore || evaluationsHook.isLoadingMore;

	const hasError =
		!!tracesHook.error ||
		!!evaluationsHook.error ||
		!!statsHook.error ||
		!!categoriesHook.error;

	return {
		traces: tracesHook.traces,
		evaluations: evaluationsHook.evaluations,
		stats: statsHook.stats,
		categories: categoriesHook.categories,
		isLoading,
		isLoadingMore,
		hasError,
		errors: {
			traces: tracesHook.error,
			evaluations: evaluationsHook.error,
			stats: statsHook.error,
			categories: categoriesHook.error,
		},
		progress: {
			tracesLoaded: tracesHook.chunksLoaded || 0,
			tracesTotal: tracesHook.totalChunks || 0,
			evaluationsLoaded: evaluationsHook.chunksLoaded || 0,
			evaluationsTotal: evaluationsHook.totalChunks || 0,
		},
		refreshAll,
		refreshTraces: tracesHook.refreshData,
		refreshEvaluations: evaluationsHook.refreshData,
		refreshStats: statsHook.refreshData,
		refreshCategories: categoriesHook.refreshData,
	};
};
